<script setup lang="ts">
import { getPickupPoints } from '@caks/shared/api/user.route'
import { useAccountStore } from '@caks/shared/store/store.account'
import { PickupPoint } from '@caks/shared/types/types.api'
import { ChipTag } from '@caks/uikit/chip'
import { ILMap } from '@caks/shared/modules/i'

const store = useAccountStore()
const isMount = ref(false)
const coordinates = ref<PickupPoint[]>([])

onMounted(async () => {
  isMount.value = true

  const cityId = localStorage.getItem('caksCityId') as string
  const { data } = await getPickupPoints(+cityId)

  coordinates.value = data
})

watch(
  () => store.cityId,
  async (newValue) => {
    if (newValue !== null) {
      coordinates.value = []
      const { data } = await getPickupPoints(newValue)

      coordinates.value = data
    }
  }
)
</script>

<template>
  <div class="contacts">
    <h1 class="contacts__title">{{ store.city }}</h1>
    <div class="contacts__top">
      <div class="contacts__map">
        <i-l-map
          v-if="coordinates.length"
          :lon="coordinates[0].lon"
          :lat="coordinates[0].lat"
          :points="coordinates"
          desktop
        />
        <skeletor v-else style="border-radius: 0; width: 100%" />
      </div>
      <div class="contacts__right-block">
        <div class="contacts__main-info main-info">
          <div class="main-info__block">
            <h6>Телефон:</h6>
            <p class="text">8 800 700 66 87</p>
          </div>
          <div class="main-info__block">
            <h6>Вопросы, отзывы и предложения:</h6>
            <p class="text">support@sacksgroup.ru</p>
          </div>
        </div>
        <div class="contacts__main-info main-info">
          <div class="main-info__block">
            <h6>Индивидуальный предприниматель</h6>
            <p class="text">Минасян Саркис Петросович</p>
          </div>
          <div class="main-info__block">
            <h6>ИНН/ОГРНИП:</h6>
            <p class="text">263216977408/317265100078605</p>
          </div>
        </div>
        <div class="contacts__main-info main-info">
          <div class="main-info__block">
            <h6>Индивидуальный предприниматель</h6>
            <p class="text">Година Галина Григорьевна</p>
          </div>
          <div class="main-info__block">
            <h6>ИНН/ОГРНИП:</h6>
            <p class="text">263203003101/323265100173818</p>
          </div>
        </div>
        <div class="contacts__main-info main-info">
          <div class="main-info__block">
            <h6>Индивидуальный предприниматель</h6>
            <p class="text">Единарова Людмила Анатольевна</p>
          </div>
          <div class="main-info__block">
            <h6>ИНН/ОГРНИП:</h6>
            <p class="text">263201346880/323265100173829</p>
          </div>
        </div>
      </div>
    </div>
    <div class="contacts__bottom">
      <div v-for="point in coordinates" :key="point.id" class="contacts__point">
        <h3>{{ point.title }}</h3>
        <p>{{ point.name }}</p>
        <ul class="info-lines">
          <li>
            <p>На вынос</p>
            <p class="text">круглосуточно</p>
          </li>
          <li>
            <p>Доставка</p>
            <p class="text">доставка на сайте временно не доступна</p>
          </li>
          <li>
            <p>Телефон</p>
            <p class="text">8 (800) 700-66-87</p>
          </li>
        </ul>
      </div>
    </div>
  </div>
  <teleport v-if="isMount" to=".sub-header__chips">
    <li
      v-if="store.categories.length"
      v-for="(food, index) of store.categories"
      :id="`chip-index-${index}`"
      ref="categoriesChipsElements"
      class="category-chip"
    >
      <chip-tag bigger @click="$router.push('/')">
        {{ food.name }}
      </chip-tag>
    </li>
    <div v-else class="chips-skeletons">
      <skeleton-chip v-for="_ in 6" />
    </div>
  </teleport>
</template>

<style scoped lang="scss">
.contacts {
  display: flex;
  flex-direction: column;
  gap: 32px;
  max-width: 1336px;
  margin: 60px auto 0;

  &__title {
    @include h1-1;
  }

  &__top {
    display: flex;
    gap: 32px;
  }

  &__map {
    height: 790px;
    width: 100%;
  }

  &__right-block {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .main-info {
    display: flex;
    flex-direction: column;
    gap: 20px;
    border-radius: 20px;
    padding: 24px;
    background-color: $ui-gray-1;
    height: fit-content;
    min-width: 304px;

    &__block {
      display: flex;
      flex-direction: column;
      gap: 8px;

      @include h5-2;

      h6 {
        color: $sub-text;
      }

      p {
        color: $primary-text;
      }
    }
  }

  &__bottom {
    display: flex;
    flex-wrap: wrap;
    gap: 32px;
    padding-bottom: 32px;

    &::-webkit-scrollbar {
      width: 4px;
      height: 12px;
    }

    &::-webkit-scrollbar-track {
      background: $ui-gray-2;
      border-radius: 10px;
    }

    &::-webkit-scrollbar-thumb {
      background-color: $sub-text;
      border-radius: 10px;
      border: 2px solid $ui-gray-2;
    }
  }

  &__point {
    display: flex;
    flex-direction: column;
    gap: 12px;
    height: fit-content;
    min-width: 374px;

    ul {
      display: flex;
      flex-direction: column;
      gap: 12px;
      margin-top: 12px;
    }

    h3 {
      @include h3-5;
    }

    p {
      @include h5-2;
      color: $sub-text;
    }

    li {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
    }

    .text {
      @include h5-2;
      color: $primary-text;
    }
  }
}
</style>
